import { useState } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import CanonicalTag from "@/components/CanonicalTag";

const ModernFarmhouseADUDesignHayward = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  const openCalendlyModal = () => {
    setShowCalendly(true);
  };

  const closeCalendlyModal = () => {
    setShowCalendly(false);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Modern Farmhouse ADU Design & Construction in Hayward",
    "description": "Custom Modern Farmhouse ADUs in Hayward. Expert design & construction by UDA ADU. Get your free consultation today.",
    "publisher": {
      "@type": "Organization",
      "name": "UDA ADU",
      "url": "https://www.udaadu.com"
    },
    "url": "https://www.udaadu.com/modern-farmhouse-adu-design-hayward"
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/modern-farmhouse-adu-design-hayward" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData)
      }} />
      
      <Navigation />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-4xl h-[90vh] bg-white rounded-lg overflow-hidden">
            <button 
              onClick={closeCalendlyModal}
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Schedule a meeting"
            />
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-900 to-green-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Modern Farmhouse ADU Design in Hayward
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-green-100">
              Create stunning Modern Farmhouse ADUs that blend rustic charm with contemporary comfort in Hayward
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={openCalendlyModal}
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 text-lg font-semibold rounded-lg transition-colors"
              >
                Get Free Design Consultation
              </Button>
              <Button 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-green-900 px-8 py-3 text-lg font-semibold rounded-lg transition-colors"
              >
                View Portfolio
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Modern Farmhouse Features */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
              Modern Farmhouse ADU Design Elements
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 text-green-600">Board & Batten Siding</h3>
                <p className="text-gray-600">
                  Classic vertical wood siding that creates texture and visual interest while providing excellent durability for Hayward's climate.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 text-green-600">Metal Roofing</h3>
                <p className="text-gray-600">
                  Energy-efficient standing seam metal roofs that provide longevity and complement the farmhouse aesthetic.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 text-green-600">Large Windows</h3>
                <p className="text-gray-600">
                  Floor-to-ceiling windows and sliding glass doors that maximize natural light and create seamless indoor-outdoor living.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 text-green-600">Covered Porches</h3>
                <p className="text-gray-600">
                  Welcoming front porches with exposed beam ceilings that extend living space and create charming outdoor areas.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 text-green-600">Exposed Beams</h3>
                <p className="text-gray-600">
                  Reclaimed wood ceiling beams that add warmth and rustic character to modern open-concept living spaces.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 text-green-600">Neutral Color Palettes</h3>
                <p className="text-gray-600">
                  Sophisticated whites, grays, and natural wood tones that create a timeless and calming aesthetic.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hayward Specific Benefits */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
              Why Modern Farmhouse ADUs Work in Hayward
            </h2>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-semibold mb-6 text-green-600">Perfect for Hayward Living</h3>
                <ul className="space-y-4 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    Complements Hayward's diverse architectural landscape
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    Energy-efficient design reduces utility costs
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    Low-maintenance materials perfect for busy lifestyles
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    Flexible design adapts to various lot sizes
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-6 text-green-600">Investment Benefits</h3>
                <ul className="space-y-4 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    Increases property value by 20-30%
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    Rental income potential up to $3,800/month
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    High tenant demand for modern amenities
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    Meets all Alameda County building requirements
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Design Process */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-800">
              Our Modern Farmhouse ADU Design Process
            </h2>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="bg-green-600 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
                <h3 className="text-xl font-semibold mb-3">Site Assessment</h3>
                <p className="text-gray-600">Evaluate your Hayward property for optimal ADU placement and design opportunities.</p>
              </div>
              <div className="text-center">
                <div className="bg-green-600 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
                <h3 className="text-xl font-semibold mb-3">Custom Design</h3>
                <p className="text-gray-600">Create Modern Farmhouse plans that blend rustic charm with contemporary functionality.</p>
              </div>
              <div className="text-center">
                <div className="bg-green-600 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
                <h3 className="text-xl font-semibold mb-3">Permit Process</h3>
                <p className="text-gray-600">Navigate Hayward's permitting requirements with our expert local knowledge.</p>
              </div>
              <div className="text-center">
                <div className="bg-green-600 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">4</div>
                <h3 className="text-xl font-semibold mb-3">Quality Build</h3>
                <p className="text-gray-600">Construct your Modern Farmhouse ADU with attention to detail and quality craftsmanship.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-green-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Build Your Modern Farmhouse ADU in Hayward?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Let's create a beautiful Modern Farmhouse ADU that enhances your property value and provides the perfect blend of rustic charm and modern comfort.
          </p>
          <Button 
            onClick={openCalendlyModal}
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg font-semibold rounded-lg transition-colors"
          >
            Schedule Your Free Consultation
          </Button>
        </div>
      </section>

      <FloatingADUWizard />
      <Footer />
    </div>
  );
};

export default ModernFarmhouseADUDesignHayward;